import type { GridColDef } from '@mui/x-data-grid';
import { REPORT_JOB_TYPES, type ReportData } from '@weft/types';
import { isEmptyString, isNotNullOrUndefined, isNullOrUndefined } from '@weft/utils';
import { ReportJobStatusChip } from 'apps/app/src/components/design/ReportJobStatusChip';
import { DownloadReportButton } from '../../report-jobs-section/report-jobs-table/DownloadReportButton';
import { OpenReportButton } from '../../report-jobs-section/report-jobs-table/OpenReportButton';
import { getTableCell } from '../../report-jobs-section/report-jobs-table/ReportJobsTableColumns';
import type { ReportJobsDownloadProps } from '../../report-jobs-section/ReportJobsSection';
import { getReportJobType } from '../../ReportsPageContent';
import { NavigateToReportTypeButton } from '../all-reports-components/NavigateToReportTypeButton';

export const ALL_REPORTS_COLUMNS_KEYS = Object.freeze({
  STATUS: 'status',
  NAME: 'reportName',
  REPORT_TYPE: 'reportType',
  OPEN_REPORT: 'openReport',
} satisfies Record<string, keyof ReportData | 'reportType' | 'openReport'>);

export type AllReportsColumnKeys = keyof typeof ALL_REPORTS_COLUMNS_KEYS;

export const getAllReportsColumns = (onDownload?: ReportJobsDownloadProps['onDownload']): GridColDef[] => [
  {
    field: ALL_REPORTS_COLUMNS_KEYS.STATUS,
    headerName: 'Status',
    renderCell: ({ row }) => <ReportJobStatusChip status={row.status}/>,
    headerClassName: 'text-sm',
    headerAlign: 'center',
    align: 'center',
    cellClassName: 'pl-10',
    width: 160,
  },
  {
    field: ALL_REPORTS_COLUMNS_KEYS.REPORT_TYPE,
    headerName: 'Report type',
    headerClassName: 'text-sm',
    flex: 1,
    headerAlign: 'center',
    align: 'center',
    renderCell: ({ row }) => getTableCell(row.type),
  },
  {
    field: ALL_REPORTS_COLUMNS_KEYS.NAME,
    headerName: 'Report name',
    headerClassName: 'text-sm',
    flex: 1,
    width: 400,
    headerAlign: 'left',
    align: 'left',
    renderCell: ({ row: { reportName } }) => getTableCell(isEmptyString(reportName) ? '--' : reportName),
  },
  {
    field: ALL_REPORTS_COLUMNS_KEYS.OPEN_REPORT,
    headerName: '',
    width: 200,
    flex: 1,
    sortable: false,
    cellClassName: 'pr-10',
    align: 'right',
    renderCell: ({ row: { status, id, type } }) => {
      const reportJobType = getReportJobType(type);
      const isCustomerRevenueWithDownload = reportJobType === REPORT_JOB_TYPES.CustomerRevenue && isNotNullOrUndefined(onDownload);

      if (isNullOrUndefined(reportJobType)) {
        return <NavigateToReportTypeButton type={type} />;
      }

      if (isCustomerRevenueWithDownload) {
        return <DownloadReportButton onClick={() => onDownload(id)}/>;
      }
      
      return (
        <OpenReportButton
          reportId={id}
          status={status}
          type={reportJobType}
        />
      );
    },
  },
];
